import { Box, Spinner, Stack, Td, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import type { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SavingsList,
  WithoutSavingReason,
  useGetContractSavingsQuery,
  type ContractSavingDto,
} from "../../autogen/bff-api";
import { ListLoadError } from "../../common/ListLoadError";
import { Paging } from "../../common/Paging";
import { useApiError } from "../../common/errors/useApiError";
import { urls } from "../../urls";
import { PanelRow, PanelTable, TitleCell } from "../home/panels/PanelTable";
import { formatMoney, formatShare, savingsLabel, withoutSavingLabel } from "./savingsFigures";

const LISTED_CONTRACTS = 10;

type SavingsQuery = { orgId: string; from: string; to: string };

type ReasonLine = { baseline?: number | null; awarded?: number | null; deviation: string };

export const WithoutSavings = ({
  query,
  count,
  anyMeasured,
  currency,
  opened,
  onShown,
}: {
  query: SavingsQuery;
  count: number;
  anyMeasured: boolean;
  currency: string;
  opened: boolean;
  onShown: () => void;
}) => {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(0);
  const { data, isLoading, isFetching, error, refetch } = useGetContractSavingsQuery(
    count > 0 ? { ...query, list: SavingsList.WithoutSaving, limit: LISTED_CONTRACTS, skip } : skipToken,
  );
  const { message } = useApiError();
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!opened) return;
    card.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    onShown();
  }, [opened, onShown]);
  const listed = data?.listed;
  const money = (value?: number | null) => (value == null ? "—" : formatMoney(value, currency));

  return (
    <Stack
      ref={card}
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="without-savings"
    >
      <Text as="h2" fontWeight="semibold" color="smText">
        {t("{{label}} ({{count}} contracts)", { label: savingsLabel(t, SavingsList.WithoutSaving), count: listed?.total ?? count })}
      </Text>
      {count === 0 ? (
        <Text fontSize="sm" color="smMuted" data-testid="without-savings-empty">
          {anyMeasured
            ? t("All contracts in the period have a saving")
            : t("No contract in the period has an estimated value or a first bid to measure against.")}
        </Text>
      ) : isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !listed ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <>
          <Box overflowX="auto">
            <PanelTable
              headers={[
                String(t("Contract")),
                String(t("Estimated value / First bid")),
                String(t("Value at award / Last round")),
                String(t("Difference")),
              ]}
            >
              {listed.contracts.map((contract) => {
                const lines = reasonLines(t, contract, money);
                return (
                  <PanelRow key={contract.contractId} to={urls.app.contracts.view.go(contract.contractId)}>
                    <TitleCell
                      title={contract.title || String(t("Untitled"))}
                      subtitle={contract.supplierName ?? undefined}
                    />
                    <LinesCell lines={lines.map((line) => money(line.baseline))} />
                    <LinesCell lines={lines.map((line) => money(line.awarded))} />
                    <LinesCell lines={lines.map((line) => line.deviation)} testId={`deviation-${contract.contractId}`} />
                  </PanelRow>
                );
              })}
            </PanelTable>
          </Box>
          {listed.total > LISTED_CONTRACTS && (
            <Paging
              limit={LISTED_CONTRACTS}
              skip={skip}
              setSkip={setSkip}
              isLoading={isFetching}
              count={listed.total}
            />
          )}
        </>
      )}
    </Stack>
  );
};

const LinesCell = ({ lines, testId }: { lines: string[]; testId?: string }) => (
  <Td data-testid={testId}>
    <Stack spacing="0">
      {lines.map((line, index) => (
        <Text key={index} fontSize="sm" color="smMuted">
          {line}
        </Text>
      ))}
    </Stack>
  </Td>
);

const reasonLines = (
  t: TFunction,
  contract: ContractSavingDto,
  money: (value?: number | null) => string,
): ReasonLine[] =>
  (contract.withoutSaving ?? []).map((reason) => {
    const estimate = { baseline: contract.estimatedValue, awarded: contract.awardedValue };
    const negotiation = { baseline: contract.firstBid, awarded: contract.lastRound };
    const amount = (value?: number | null) => money(value == null ? null : Math.abs(value));
    const share = (value?: number | null) => (value == null ? "—" : formatShare(value));
    switch (reason) {
      case WithoutSavingReason.AboveEstimate:
        return {
          ...estimate,
          deviation: t("Above estimated value: {{value}} ({{share}})", {
            value: amount(contract.estimateDifference),
            share: share(contract.estimateShare),
          }),
        };
      case WithoutSavingReason.AtEstimate:
        return { ...estimate, deviation: withoutSavingLabel(t, reason) };
      case WithoutSavingReason.UnchangedInNegotiation:
        return { ...negotiation, deviation: withoutSavingLabel(t, reason) };
      case WithoutSavingReason.AboveFirstBid:
        return {
          ...negotiation,
          deviation: t("Above the first bid: {{value}} ({{share}})", {
            value: amount(contract.savedInNegotiation),
            share: share(contract.negotiationShare),
          }),
        };
    }
  });
